import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { RideAlongService } from '../../../../services/ride-along.service';
import { AuthService } from '../../../../core/auth/auth.service';
import { getWaypointIconAsset } from '../../../../shared/utils/waypoint-icon.util';

interface WaypointRow {
  raw: any;
  id: string;
  title: string;
  icon: string;
  timeAgo: string;
  createdMs: number;
  creatorName: string;
  canDelete: boolean;
  deleting: boolean;
}

/**
 * Ride Along Added Waypoints modal — web equivalent of the Flutter
 * AddedWaypointsSheet. Lists the trip's waypoints split into "Mine" and "Others"
 * tabs. Opened from the follow card's "Added Waypoints" button.
 *
 * Row layout mirrors the app: a relative timestamp instead of coordinates, a
 * Remove action on rows the backend marked `can_delete`, and a creator-name tag
 * under other mates' waypoints.
 */
@Component({
  selector: 'app-ride-along-waypoints',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ride-along-waypoints.component.html',
  styleUrl: './ride-along-waypoints.component.scss',
})
export class RideAlongWaypointsComponent implements OnInit {
  @Input() tripId = '';
  @Output() close = new EventEmitter<void>();
  /** Lets the map drop a pin that was just removed here. */
  @Output() waypointRemoved = new EventEmitter<string>();

  tab: 'mine' | 'others' = 'mine';
  loading = true;
  error = '';
  mine: WaypointRow[] = [];
  others: WaypointRow[] = [];
  /** Row pending delete confirmation. */
  confirmingId: string | null = null;

  private myIds = new Set<string>();
  /** id (lowercased) → display name, resolved from the trip mates roster. */
  private participantNames = new Map<string, string>();

  constructor(private rideAlongService: RideAlongService, private authService: AuthService) {}

  ngOnInit(): void {
    [this.authService.getUserIdFromToken(), this.authService.getUserObjectId()]
      .filter((v): v is string => !!v)
      .forEach((v) => this.myIds.add(String(v).toLowerCase()));

    if (!this.tripId) {
      this.loading = false;
      this.error = 'No trip selected.';
      return;
    }
    this.load();
  }

  private load(): void {
    // Mates are fetched alongside the waypoints so "Others" rows can be tagged with a
    // real name rather than an id — same source as the Trip Mates list, like the app.
    forkJoin({
      waypoints: this.rideAlongService.getRideAlongWaypoints(this.tripId),
      mates: this.rideAlongService.getTripMates(this.tripId).pipe(
        catchError(() => of({ captain: null, participants: [] })),
      ),
    }).subscribe({
      next: ({ waypoints, mates }) => {
        this.indexParticipantNames(mates);
        this.buildRows(waypoints || []);
        this.loading = false;
      },
      error: () => {
        this.error = 'Failed to load waypoints.';
        this.loading = false;
      },
    });
  }

  private indexParticipantNames(mates: { captain: any; participants: any[] }): void {
    this.participantNames.clear();
    const index = (p: any) => {
      if (!p?.name) return;
      [p.profileId, p.keycloakId].forEach((id) => {
        const k = String(id || '').trim().toLowerCase();
        if (k) this.participantNames.set(k, p.name);
      });
    };
    if (mates?.captain) index(mates.captain);
    (mates?.participants || []).forEach(index);
  }

  private buildRows(wps: any[]): void {
    const mine: WaypointRow[] = [];
    const others: WaypointRow[] = [];
    wps.forEach((wp) => {
      const row = this.toRow(wp);
      (this.isMine(wp) ? mine : others).push(row);
    });
    // Newest first — matches the app's sort.
    const byNewest = (a: WaypointRow, b: WaypointRow) => b.createdMs - a.createdMs;
    this.mine = mine.sort(byNewest);
    this.others = others.sort(byNewest);
  }

  private toRow(wp: any): WaypointRow {
    const createdMs = this.createdMs(wp);
    return {
      raw: wp,
      id: this.waypointId(wp),
      title: this.title(wp),
      icon: this.icon(wp),
      timeAgo: this.formatTimeAgo(createdMs),
      createdMs,
      creatorName: this.creatorName(wp),
      // The backend decides: captain can remove anything, riders only their own.
      canDelete: wp?.can_delete === true,
      deleting: false,
    };
  }

  get active(): WaypointRow[] {
    return this.tab === 'mine' ? this.mine : this.others;
  }

  setTab(tab: 'mine' | 'others'): void {
    this.tab = tab;
    this.confirmingId = null;
  }

  onClose(): void {
    this.close.emit();
  }

  askRemove(row: WaypointRow): void {
    this.confirmingId = row.id;
  }

  cancelRemove(): void {
    this.confirmingId = null;
  }

  confirmRemove(row: WaypointRow): void {
    if (!row.id || row.deleting) return;
    row.deleting = true;
    this.confirmingId = null;
    this.rideAlongService.removeRideAlongWaypoint(this.tripId, row.id).subscribe({
      next: () => {
        this.mine = this.mine.filter((r) => r.id !== row.id);
        this.others = this.others.filter((r) => r.id !== row.id);
        this.waypointRemoved.emit(row.id);
      },
      error: () => {
        row.deleting = false;
        this.error = 'Failed to remove waypoint.';
      },
    });
  }

  private icon(wp: any): string {
    // 'card' returns the rounded "_tab" icons (e.g. caught_yellowfin_tab.png) —
    // the fully-round badges the app's AddedWaypointsSheet uses, which fill the
    // circle. The 'map' variant is the tiny map-pin dot and looked lost here.
    try {
      return `/${getWaypointIconAsset(wp, 'card')}`.replace('//assets', '/assets');
    } catch {
      return '/assets/common_icons/waypoint_pin_white.svg';
    }
  }

  private title(wp: any): string {
    if (wp?.name) return wp.name;
    const type = (wp?.type || 'Waypoint').toString().replace(/_/g, ' ');
    return type.charAt(0).toUpperCase() + type.slice(1);
  }

  private waypointId(wp: any): string {
    return String(wp?._id || wp?.id || wp?.waypoint_id || wp?.identifier || '').trim();
  }

  private createdMs(wp: any): number {
    const raw = wp?.audit_data?.create_ts || wp?.created_at || wp?.create_ts;
    if (!raw) return 0;
    // Timestamps come back without a zone designator; they are UTC.
    const s = String(raw);
    const iso = /[zZ]|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`;
    const ms = Date.parse(iso);
    return isNaN(ms) ? 0 : ms;
  }

  /** Mirrors _formatTimeAgo in added_waypoints_sheet.dart. */
  private formatTimeAgo(ms: number): string {
    if (!ms) return '';
    const diffMs = Date.now() - ms;
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}hr ago`;
    const days = Math.floor(hours / 24);
    const time = new Date(ms)
      .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
      .toLowerCase();
    if (days < 7) {
      if (days === 1) return `yesterday ${time}`;
      return `${days} days ago at ${time}`;
    }
    const date = new Date(ms).toLocaleDateString('en-US', {
      month: '2-digit', day: '2-digit', year: '2-digit',
    });
    return `${time} ${date}`;
  }

  private creatorName(wp: any): string {
    const direct =
      wp?.creator?.display_name ||
      wp?.source_profile?.name ||
      wp?.details?.creator_name ||
      wp?.reference?.creator_name;
    if (direct) return String(direct);

    const ids = [
      wp?.audit_data?.create_user_id,
      wp?.source_profile?.id,
      wp?.reference?.creator_profile_id,
      wp?.reference?.creator_keycloak_id,
    ];
    for (const id of ids) {
      const k = String(id || '').trim().toLowerCase();
      const name = k ? this.participantNames.get(k) : undefined;
      if (name) return name;
    }
    return 'Unknown';
  }

  private isMine(wp: any): boolean {
    const creator = wp?.audit_data?.create_user_id || wp?.source_profile?.id || wp?.creator;
    return !!creator && this.myIds.has(String(creator).toLowerCase());
  }
}
